import React from 'react'
import {
    CircularProgress,
    Collapse,
    IconButton,
    makeStyles
} from '@material-ui/core'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff'
import VisibilityIcon from '@material-ui/icons/Visibility'
import { useWizard } from 'react-use-wizard'
import DoneIcon from '@material-ui/icons/Done'
import {
    Button,
    InfoBlock,
    Input,
    PageTitle,
    Picker
} from 'web/src/app/root/components/common'
import {
    LOCALE_MAP,
    LOCALE_MAP_REVERSED,
    RELATIONS
} from 'web/src/app/root/constants'
import { useState } from 'react'
import { generatePassword, isEmailString } from 'web/src/utils'
import { useBooleanState } from 'web/src/utils/hooks/useBooleanState'
import { useSelector } from 'react-redux'
import { useMemo } from 'react'
import { debounce } from 'lodash'
import { checkUserByEmail } from 'web/src/app/user-management/api/api'
import {
    getNonExistentUserDataForValidation,
    useCreateUser
} from '../../context/create-user-context'
import { PasswordInput } from 'web/src/app/root/components/common/password-input/password-input'
import { usePasswordValidate } from 'web/src/app/root/components/common/password-input/use-password-validate'
import { useRef } from 'react'
import { useEffect } from 'react'
import { useCallback } from 'react'
import { NavLink } from 'react-router-dom'

export const UserCredentials = () => {
    const styles = useStyles()
    const { previousStep, nextStep } = useWizard()
    const {
        userState,
        onUserUpdate,
        userRelation,
        setUserRelationFromEmail,
        userRelationFromEmail
    } = useCreateUser()
    const { localeCode, locale } = useSelector(state => state.main)
    const parsedInitialState = parseInitialState(userState, localeCode)
    const checkingEmail = useBooleanState(false)
    const passwordRef = useRef()

    const pristine = useBooleanState(true)
    const passwordVisible = useBooleanState(false)
    const localeOptions = useMemo(() => makeOptions(LOCALE_MAP), [])

    const isNonExistent = userRelationFromEmail === RELATIONS.nonexistent
    const isExternalOut = userRelationFromEmail === RELATIONS.externalOut
    const isExternalIn = userRelationFromEmail === RELATIONS.externalIn
    const isInternal = userRelationFromEmail === RELATIONS.internal

    const isSelectedExternal = userRelation === 'external'
    const isCreateCustomerName = isSelectedExternal && isNonExistent
    const isEmailOk = isExternalOut || isNonExistent

    const [state, setState] = useState(parsedInitialState)

    const { validators, isPasswordValid } = usePasswordValidate(state.password)

    const handleValueChange = fieldName => v => {
        setState(s => ({
            ...s,
            [fieldName]: v
        }))
    }

    const handleGeneratePassword = () =>
        handleValueChange('password')(generatePassword())

    const isValid = () => {
        //user already exists
        if (isExternalIn || isInternal) {
            return false
        }
        if (!isEmailString(state.email)) {
            return false
        }
        if (isExternalOut) {
            return true
        }
        if (!isPasswordValid) {
            passwordRef.current.focus()
            return false
        }
        if (isNonExistent) {
            const dataForValidation = getNonExistentUserDataForValidation(
                state,
                userRelation
            )
            return Object.values(dataForValidation).every(Boolean)
        }
        return false
    }
    const handlePrevStep = () => {
        setUserRelationFromEmail('')
        previousStep()
    }
    const handleNextStep = () => {
        pristine.off()

        if (!isValid()) {
            return
        }

        const { name, lastName, ...restState } = state
        let fullName = `${name} ${lastName}`
        if (isExternalOut) fullName = undefined
        onUserUpdate({ ...restState, name: fullName })

        nextStep()
    }

    const checkIsEmailExist = useCallback(
        debounce(v => {
            if (isEmailString(v)) {
                checkingEmail.on()

                checkUserByEmail(v)
                    .then(r => {
                        setUserRelationFromEmail(r)
                    })
                    .finally(checkingEmail.off)
            }
        }, 800),
        []
    )

    useEffect(() => {
        if (userState?.email) {
            checkIsEmailExist(userState?.email)
        }
    }, [])

    return (
        <div className={styles.root}>
            <PageTitle
                position={'center'}
                title={locale.userManagement.credentialsStepTitle}
            />
            <div className={styles.emailRoot}>
                <Input
                    autoFocus
                    errorText={locale.common.notValidEmail}
                    isError={!state.email || !isEmailString(state.email)}
                    value={state.email}
                    setValue={v => {
                        const email = v.trim()
                        handleValueChange('email')(email)
                        checkIsEmailExist(email)
                    }}
                    // disabled={checkingEmail.is}
                    label={locale.common.email}
                    endIcon={() =>
                        checkingEmail.is && (
                            <CircularProgress
                                size={12}
                                className={styles.endIcon}
                            />
                        )
                    }
                />
                {isEmailOk && <DoneIcon className={styles.doneIcon} />}
            </div>
            <div className={styles.emailHelper}>
                <CheckResultMessage
                    {...{
                        isSelectedExternal,
                        locale,
                        userRelationFromEmail,
                        checkingEmail,
                        email: state.email,
                        styles
                    }}
                />
            </div>
            <Collapse in={isNonExistent}>
                <div className={styles.columnsContainer}>
                    <Input
                        errorText={locale.common.required}
                        isError={!pristine.is && !state.name}
                        value={state.name}
                        setValue={handleValueChange('name')}
                        label={locale.userManagement.firstName}
                        disabled={checkingEmail.is}
                        inputProps={{
                            autoComplete: 'new-password'
                        }}
                    />
                    <Input
                        errorText={locale.common.required}
                        isError={!pristine.is && !state.lastName}
                        value={state.lastName}
                        setValue={handleValueChange('lastName')}
                        label={locale.userManagement.lastName}
                        disabled={checkingEmail.is}
                        inputProps={{
                            autoComplete: 'new-password'
                        }}
                    />
                    <PasswordInput
                        validators={validators}
                        inputRef={passwordRef}
                        errorText={locale.common.passwordErrorMessage}
                        isError={
                            !pristine.is &&
                            (!state.password || !isPasswordValid)
                        }
                        type={passwordVisible.is ? 'text' : 'password'}
                        value={state.password}
                        setValue={handleValueChange('password')}
                        label={locale.common.password}
                        inputProps={{
                            autoComplete: 'new-password'
                        }}
                        endIcon={() => (
                            <IconButton
                                classes={{ root: styles.showPassIcon }}
                                className={styles.endIcon}
                                onClick={() => passwordVisible.toggle()}
                            >
                                {passwordVisible.is ? (
                                    <VisibilityOffIcon />
                                ) : (
                                    <VisibilityIcon />
                                )}
                            </IconButton>
                        )}
                        disabled={checkingEmail.is}
                    />
                    <div>
                        <Button
                            disabled={checkingEmail.is}
                            onClick={handleGeneratePassword}
                        >
                            {locale.userManagement.genPassword}
                        </Button>
                    </div>
                    {isCreateCustomerName ? (
                        <>
                            <Input
                                errorText={locale.common.required}
                                isError={!pristine.is && !state.customer_name}
                                value={state.customer_name}
                                setValue={handleValueChange('customer_name')}
                                label={locale.userManagement.customerName}
                            />
                            <div />
                        </>
                    ) : null}

                    <Picker
                        className={styles.langPicker}
                        disablePortal
                        onChange={handleValueChange('language')}
                        value={state.language}
                        options={localeOptions}
                        disabled={checkingEmail.is}
                    />
                </div>
            </Collapse>
            <div className={styles.buttonsBlock}>
                <Button
                    disabled={checkingEmail.is}
                    reverted
                    onClick={handlePrevStep}
                >
                    {locale.userManagement.stepBack}
                </Button>
                {userRelationFromEmail &&
                (userRelationFromEmail === RELATIONS.externalIn ||
                    userRelationFromEmail === RELATIONS.internal) ? null : (
                    <Button
                        disabled={checkingEmail.is}
                        onClick={handleNextStep}
                    >
                        {locale.userManagement.nextStep}
                    </Button>
                )}
            </div>
        </div>
    )
}

const CheckResultMessage = ({
    isSelectedExternal,
    locale,
    userRelationFromEmail,
    checkingEmail,
    email,
    styles
}) => {
    if (!checkingEmail.is && userRelationFromEmail && !isSelectedExternal) {
        if (userRelationFromEmail === RELATIONS.externalIn) {
            return (
                <InfoBlock>
                    {
                        locale.userManagement.userRelationExplanation[
                            RELATIONS.externalIn
                        ]
                    }
                    ,
                    <a
                        className={styles.link}
                        href={`/user-management/users?search=${email}&membership=all`}
                    >
                        {` ${locale.userManagement.showTheUserInTheList}`}
                    </a>
                </InfoBlock>
            )
        }
        if (userRelationFromEmail === RELATIONS.externalOut) {
            return (
                <InfoBlock>
                    {
                        locale.userManagement.userRelationExplanation[
                            RELATIONS.externalOut
                        ]
                    }
                </InfoBlock>
            )
        }
        if (userRelationFromEmail === RELATIONS.internal) {
            return (
                <InfoBlock>
                    {
                        locale.userManagement.userRelationExplanation[
                            RELATIONS.internal
                        ]
                    }
                    ,
                    <a
                        className={styles.link}
                        href={`/user-management/users?search=${email}&membership=all`}
                    >
                        {` ${locale.userManagement.showTheUserInTheList}`}
                    </a>
                </InfoBlock>
            )
        }
    }
    if (!checkingEmail.is && userRelationFromEmail && isSelectedExternal) {
        if (userRelationFromEmail === RELATIONS.externalIn) {
            return (
                <InfoBlock>
                    {
                        locale.userManagement.userRelationExplanation[
                            RELATIONS.externalIn
                        ]
                    }
                    ,
                    <a
                        className={styles.link}
                        href={`/user-management/users?search=${email}&membership=all`}
                    >
                        {` ${locale.userManagement.showTheUserInTheList}`}
                    </a>
                </InfoBlock>
            )
        }
        if (userRelationFromEmail === RELATIONS.externalOut) {
            return (
                <InfoBlock noIcon>
                    {locale.userManagement.youCanProceedWithRoleSelection}
                </InfoBlock>
            )
        }
        if (userRelationFromEmail === RELATIONS.internal) {
            return (
                <InfoBlock>
                    {
                        locale.userManagement.userRelationExplanation[
                            RELATIONS.internal
                        ]
                    }
                    ,
                    <a
                        className={styles.link}
                        href={`/user-management/users?search=${email}&membership=all`}
                    >
                        {` ${locale.userManagement.showTheUserInTheList}`}
                    </a>
                </InfoBlock>
            )
        }
    }
    return null
}

const parseInitialState = (state, localeCode) => {
    const initialLang =
        state.language ||
        LOCALE_MAP_REVERSED[localeCode] ||
        LOCALE_MAP_REVERSED.de
    if (!state || !state.name) {
        return {
            ...state,
            language: initialLang
        }
    }
    const { name: fullName, ...restState } = state
    const [name, lastName] = fullName.split(' ')

    return {
        ...restState,
        email: state.email,
        language:
            state.language ||
            LOCALE_MAP_REVERSED[localeCode] ||
            LOCALE_MAP_REVERSED.de,
        password: state.password || '',
        name,
        lastName
    }
}

const makeOptions = localeMap => {
    for (const [key, value] of Object.entries(localeMap)) {
        localeMap[key] = value.toUpperCase()
    }
    return localeMap
}

const useStyles = makeStyles(theme => ({
    emailRoot: {
        position: 'relative',
        marginTop: 80,
        minWidth: 300
    },
    emailHelper: {
        marginTop: 24,
        display: 'flex',
        alignItems: 'center'
    },
    showPassIcon: {
        padding: 0
    },
    buttonsBlock: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        // marginTop: 'auto',
        marginTop: 20,
        marginBottom: 46,
        '&& > :last-child': {
            marginLeft: 16
        }
    },
    langPicker: {
        border: theme.border,
        padding: '0 8px',
        borderRadius: 7,
        height: 48,
        justifyContent: 'center'
    },
    columnsContainer: {
        marginTop: 60,
        display: 'grid',
        gridTemplateColumns:
            'minmax(min-content, 320px) minmax(min-content, 300px)',
        gridGap: 20
    },
    root: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        flex: 1,
        padding: '40px 94px 0'
    },
    doneIcon: {
        position: 'absolute',
        color: theme.active,
        top: 10,
        right: -35
    },
    link: {
        color: theme.textPrimary,
        textDecoration: 'none',
        fontWeight: 'bold',
        cursor: 'pointer',
        '&:hover': {
            opacity: 0.3
        }
    },
    endIcon: {
        padding: 0,
        position: 'absolute',
        right: 14
    }
}))
